/**
 * Technology Domain Create Route
 *
 * Create a new technology domain using TechnologyDomainForm component
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { TechnologyDomainForm } from '../../components/technology-domains';
import {
  createTechnologyDomain,
  type CreateTechnologyDomainInput,
  ApiError,
} from '../../lib/apiClient';
import type { CreateTechnologyDomainFormData } from '../../components/forms';
import './new.css';

export const Route = createFileRoute('/technology-domains/new')({
  component: TechnologyDomainCreatePage,
});

function TechnologyDomainCreatePage(): JSX.Element {
  const BannerElement = 'forge-banner' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (formData: CreateTechnologyDomainFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build create input
      const input: CreateTechnologyDomainInput = {
        name: formData.name,
        description: formData.description || undefined,
        tags: formData.tags && formData.tags.length > 0 ? formData.tags : undefined,
        notes: formData.notes || undefined,
      };

      const newTechnologyDomain = await createTechnologyDomain(input);

      // Refetch queries to update cache
      await apolloClient.refetchQueries({
        include: ['ListTechnologyDomains'],
      });

      // Navigate to the new technology domain detail page
      navigate({ to: `/technology-domains/${newTechnologyDomain.entityId}` as string });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: '/technology-domains', search: { page: 1, pageSize: 25 } });
  };

  return (
    <div className="technology-domain-create-page">
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Create Technology Domain</h1>
        <p className="forge-typography--body2 page-header-subtitle">Add a new technology domain</p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <TechnologyDomainForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
